/**
 * GA4 event payload builders for Native Interactive Site Map (ISM) tracking.
 *
 * Pure functions — no Redux, no DOM, no dispatch. Each returns a `{ eventName,
 * customProperties }` shape ready to spread into `googleAnalytics4Event(...)`. Most events share
 * GA4's generic `native_map_events` name, differentiated by `click_target`/`click_target1`/
 * `click_target2`/`click_target3` — the same convention
 * `pages/searchResults/components/eventTracking/googleAnalytics4EventsHandler.js` uses
 * elsewhere in this app. `buildLotSelectedEvent` and `buildCardsScrolledEvent` are the exceptions
 * in the source table — they get their own dedicated event names, not the generic one.
 *
 * @module nativeInteractiveSiteMap/services/googleAnalytics4EventBuilder
 */
import getTabTrackingValue from './tabTrackingValue';
import encodeLotAttributes from './encodeLotAttributes';

/**
 * @param {object} params
 * @param {string} params.interactionType - 'zoom_in' | 'zoom_out' | 'pan' | 'map_type_changed'
 * @param {string} params.inputMethod - 'mouse' | 'touch' | 'button'
 * @param {string} [params.mapType] - 'road' | 'satellite' — only set for map_type_changed
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildMapInteractionEvent({ interactionType, inputMethod, mapType }) {
  const customProperties = {
    click_target: interactionType,
    click_target1: inputMethod,
  };

  if (mapType) {
    customProperties.click_target2 = `map_type_${mapType}`;
  }

  return { eventName: 'native_map_events', customProperties };
}

/**
 * @param {object} params
 * @param {string|null} params.selectedChip - newly selected chip key (pill path) or the selected
 *   lot's status (lot-selection path) — becomes `search_value` on both paths
 * @param {string|null} params.previousChip - the chip key that was active before (pill path only)
 * @param {number} params.resultsCount - lot count for the current view
 * @param {number} params.cardCount - number of cards rendered in the active tab
 * @param {'lot_status_type'|'lot_selection'} params.source - what updated the panel
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildPanelUpdatedEvent({ selectedChip, previousChip, resultsCount, cardCount, source, isMobile }) {
  const customProperties = {
    click_target: 'lot_status_type_selected',
    search_value: selectedChip,
    click_target2: resultsCount,
    click_target3: cardCount,
    position: source === 'lot_selection' ? 'map_lot' : 'map_pills',
    filter_name: `panel_type_${isMobile ? 'sheet' : 'panel'}`,
  };

  if (previousChip != null) {
    customProperties.click_target1 = `previouslot_${previousChip}`;
  }

  return { eventName: 'native_map_events', customProperties };
}

/**
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildPanelScrollEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: { click_target: 'lot_status_type_scrolled' },
  };
}

/**
 * Card grid horizontal scroll — gets its own dedicated event name, unlike the other non-lot
 * events above which share the generic native_map_events name.
 * @param {object} params
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildCardsScrolledEvent({ isMobile }) {
  return {
    eventName: 'native_map_card_scrolled',
    customProperties: {
      filter_name: `panel_type_${isMobile ? 'sheet' : 'panel'}`,
    },
  };
}

/**
 * @param {object} params
 * @param {string} params.selectedTab - internal tab constant (constants.js)
 * @param {number} params.resultsCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildTabSelectedEvent({ selectedTab, resultsCount }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'available_tab_selected',
      click_target1: getTabTrackingValue(selectedTab),
      click_target2: resultsCount,
    },
  };
}

/**
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifyWhenAvailableClickedEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: { click_target: 'notify_when_available_clicked' },
  };
}

/**
 * Mobile drawer interaction — a vertical drag or the drawer being opened. Reuses the generic
 * `native_map_events` name with its own `click_target`, like the other non-lot events above.
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildDrawerDraggedEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: { click_target: 'map_sheet_dragged' },
  };
}

/**
 * @param {object} params
 * @param {object} params.lot - raw store lot object (see services/processLotMapInventory.js)
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {number} params.plansCount
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLotSelectedEvent({ lot, planId, specId, plansCount, isMobile }) {
  return {
    eventName: isMobile ? 'native_map_lot_selected_m' : 'native_map_lot_selected_d',
    customProperties: {
      plan_id: planId,
      spec_id: specId,
      lot_id: lot.lotId,
      lot_number: lot.displayLabel,
      lot_group: lot.lotGroupName,
      lot_status: lot.status,
      click_target1: encodeLotAttributes({
        plansCount,
        lotType: lot.lotType,
        lotSize: lot.lotSize,
        lotFacing: lot.lotFacing,
      }),
    },
  };
}

/**
 * Fires when the user lands on the Floor Plan Detail view (initial entry, or switching to a
 * different plan while already there). Gets its own dedicated event name, unlike
 * buildViewFloorPlanClickedEvent below which shares the generic native_map_events name — same
 * exception pattern as buildLotSelectedEvent/buildCardsScrolledEvent above.
 * @param {object} params
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @param {boolean} params.hasConfigurations - only set filter_name when true ("pass if true,
 *   blank if false" per the AC table)
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildFloorPlanViewedEvent({ planId, specId, lotId, hasConfigurations }) {
  const customProperties = {
    plan_id: planId,
    spec_id: specId,
    lot_id: lotId,
  };

  if (hasConfigurations) {
    customProperties.filter_name = 'has_configurations';
  }

  return { eventName: 'native_map_floor_plan_viewed', customProperties };
}

/**
 * Mobile-only "View floor plan" button click (Floor Plan Configuration tab).
 * @param {object} params
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildViewFloorPlanClickedEvent({ planId, specId, lotId }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'view_floor_plan_clicked',
      lot_id: lotId,
      plan_id: planId,
      spec_id: specId,
    },
  };
}

/**
 * Floor Plan Configuration tab's "No configurations available" empty state shown. Gets its own
 * dedicated event name, like buildFloorPlanViewedEvent above.
 * @param {object} params
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildEmptyConfigViewedEvent({ planId, specId, lotId }) {
  return {
    eventName: 'native_map_empty_config_viewed',
    customProperties: {
      lot_id: lotId,
      plan_id: planId,
      spec_id: specId,
      filter_name: 'no_config_available',
    },
  };
}

/**
 * "Similar homes available" accordion (under a Sold Home Card) expanded or collapsed.
 * Fires on both desktop and mobile — see plugins/tracking.plugin.js#onSimilarHomesToggled.
 * Reuses the generic native_map_events name with click_target/click_target2, like the other non-lot events
 * above, but also carries lot_id as its own raw property — the same mix buildPanelUpdatedEvent
 * above already uses (search_value/position/filter_name alongside click_target).
 * @param {object} params
 * @param {boolean} params.expanded
 * @param {number} params.lotId
 * @param {number} params.similarHomesCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildSimilarHomesToggledEvent({ expanded, lotId, similarHomesCount }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: expanded ? 'similar_homes_expanded' : 'similar_homes_collapsed',
      lot_id: lotId,
      click_target2: similarHomesCount,
    },
  };
}

/**
 * A similar-home card (under a Sold Home Card's "Similar homes available" list) was clicked.
 * Fires on both desktop and mobile. Gets its own dedicated event name, like buildLotSelectedEvent
 * and buildCardsScrolledEvent above.
 * @param {object} params
 * @param {number} params.lotId
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {number} params.position - 1-based card position within the similar-homes list
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildSimilarHomeCardClickedEvent({ lotId, planId, specId, position }) {
  return {
    eventName: 'native_map_smlr_hm_clicked',
    customProperties: { lot_id: lotId, plan_id: planId, spec_id: specId, current_card_position: position },
  };
}

/**
 * Mobile-only Sold list "See All" button clicked. Reuses the generic native_map_events name.
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildSoldSeeAllClickedEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: { click_target: 'sold_lots_see_all_clicked' },
  };
}

/**
 * User enters the Lot & Elevation detail tab for a floor plan or housing unit.
 * @param {object} params
 * @param {number} params.lotId
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {'card_click'|'map_lot'|'other'} params.position
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildDetailViewOpenedEvent({ lotId, planId, specId, position, isMobile }) {
  return {
    eventName: 'native_map_home_dv',
    customProperties: {
      lot_id: lotId,
      plan_id: planId,
      spec_id: specId,
      position,
      device: isMobile ? 'mobile' : 'desktop',
    },
  };
}

/**
 * User switched tabs in the detail view (Lot & Elevation ↔ Floor Plan Configuration).
 * Fires on both desktop and mobile for floor plan and housing unit detail views.
 * @param {object} params
 * @param {number|null} params.lotId
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {'lotAndElevation'|'floorPlanConfiguration'} params.selectedTab
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildDetailsTabSelectedEvent({ lotId, planId, specId, selectedTab, isMobile }) {
  const tabTrackingValue = selectedTab === 'lotAndElevation' ? 'lot_elevation' : 'floor_plan_configuration';
  
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'details_tab_selected',
      lot_id: lotId,
      plan_id: planId,
      spec_id: specId,
      click_target1: tabTrackingValue,
      device: isMobile ? 'mobile' : 'desktop',
    },
  };
}

/**
 * Mobile user clicked "Change Lot" button in detail view.
 * @param {object} params
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildChangeLotSelectedEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'change_lot_selected',
    },
  };
}

/**
 * User selected a different exterior elevation card.
 * @param {object} params
 * @param {number|null} params.lotId
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {string|null} params.elevationId
 * @param {string|null} params.elevationTitle
 * @param {number|null} params.position
 * @param {boolean} params.isDefault
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildExteriorSelectedEvent({
  lotId,
  planId,
  elevationId,
  elevationTitle,
  position,
  isDefault,
}) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'exterior_selected',
      click_target1: elevationId,
      click_target2: elevationTitle,
      plan_id: planId,
      lot_id: lotId,
      filter_name: isDefault ? 'is_default' : '',
      current_card_position: position,
    },
  };
}

/**
 * User expanded the exterior/elevation section in detail view.
 * @param {object} params
 * @param {number|null} params.lotId
 * @param {number|null} params.planId
 * @param {number} params.availableExteriorsCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildExteriorSelectorExpandedEvent({ lotId, planId, availableExteriorsCount }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'exterior_selector_expanded',
      plan_id: planId,
      lot_id: lotId,
      click_target1: availableExteriorsCount,
    },
  };
}

/**
 * Mobile user clicked a lot on the map while browsing in mobile lot card view.
 * Mobile-only event.
 * @param {object} params 
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildMobileLotMapClickedEvent() {
  return {
    eventName: 'native_map_lotmap_clicked_m'
  };
}

/**
 * Mobile user clicked a lot card or back button on the map.
 * Mobile-only event.
 * @param {object} params
 * @param {number|null} params.lotId
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {string|null} params.lotNumber - Display label (e.g., "140")
 * @param {string|null} params.lotGroup - Group name (e.g., "Highland View")
 * @param {string|null} params.lotStatus - Lot status (Available, Sold, etc)
 * @param {number} params.plansCount - Number of plans available for this lot
 * @param {string|null} params.type - Lot type (e.g., "Standard", "Corner")
 * @param {string|null} params.size - Formatted lot size (e.g., "0.25 acres")
 * @param {string|null} params.facing - Lot facing direction (e.g., "North")
 * @param {string} params.clickSource - 'card_click' or 'back_button'
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildMobileLotCardNavigationEvent(params) {
  const { lotId, planId, specId, lotNumber, lotGroup, lotStatus, plansCount, type, size, facing } = params;

  return {
    eventName: 'native_map_lot_selected_m',
    customProperties: {
      plan_id: planId,
      spec_id: specId,
      lot_id: lotId,
      lot_number: lotNumber,
      lot_group: lotGroup,
      lot_status: lotStatus,
      click_target1: encodeLotAttributes({
        plansCount,
        lotType: type,
        lotSize: size,
        lotFacing: facing,
      }),
    },
  };
}
